/**
 * Session-level trip counts for planning results.
 * Assigned + unassigned = total (includes in-progress trips on driver routes).
 * newAssignmentCount is always derived from live driver buckets (not backend summary)
 * so manual moves / clears stay accurate.
 */

function countAssignedFromDrivers(drivers) {
  return (drivers || []).reduce(
    (sum, d) => sum + (d.trips || []).filter((t) => !t.is_break).length,
    0
  );
}

function countNewAssignmentsFromDrivers(drivers) {
  return (drivers || []).reduce(
    (sum, d) =>
      sum +
      (d.trips || []).filter((t) => !t.is_break && t.new_assignment === true).length,
    0
  );
}

function shouldUseLiveCounts(assignmentData, loadStage) {
  if (loadStage === 'full') return true;
  if (loadStage === 'shell' || loadStage === 'lite') return false;

  const driversHaveTrips = (assignmentData?.drivers || []).some(
    (d) => (d.trips || []).length > 0
  );
  const hasUnassignedList = Array.isArray(assignmentData?.unassigned_trips);

  return driversHaveTrips || hasUnassignedList;
}

/**
 * @param {object|null|undefined} assignmentData
 * @param {{ loadStage?: 'full'|'lite'|'shell'|'idle' }} [options]
 * @returns {{ assignedCount: number, unassignedCount: number, totalCount: number, newAssignmentCount: number }}
 */
export function getSessionTripCounts(assignmentData, options = {}) {
  const summary = assignmentData?.summary || {};
  const useLive = shouldUseLiveCounts(assignmentData, options.loadStage);

  const assignedCount = useLive
    ? countAssignedFromDrivers(assignmentData?.drivers)
    : (summary.assigned_trips ?? 0);

  const unassignedCount = useLive
    ? (assignmentData?.unassigned_trips || []).length
    : (summary.unassigned_trips ?? 0);

  // Always count from current driver buckets so drag/drop / remove updates the number
  const newAssignmentCount = countNewAssignmentsFromDrivers(assignmentData?.drivers);

  return {
    assignedCount,
    unassignedCount,
    totalCount: assignedCount + unassignedCount,
    newAssignmentCount,
  };
}
